/**
 * FlightTrackLayer — renders an imported flight Track on the MapLibre map.
 *
 * The track is drawn as a LineString stroked by altitude via a data-driven
 * `interpolate` expression: greens at lower altitudes, oranges at cruise,
 * red near the flight's max. Start and end waypoints get distinct markers
 * and a scrubbable ownship marker can be positioned anywhere along the
 * track for playback-style review.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { Track } from '../services/track-parsers/track-types';

const SOURCE_ID = 'flight-track';
const LINE_LAYER_ID = 'flight-track-line';
const ENDPOINTS_SOURCE_ID = 'flight-track-endpoints';
const ENDPOINTS_LAYER_ID = 'flight-track-endpoints-circle';
const OWNSHIP_SOURCE_ID = 'flight-track-ownship';
const OWNSHIP_LAYER_ID = 'flight-track-ownship-circle';

export function trackToGeoJson(track: Track): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature<GeoJSON.LineString>[] = [];
  for (let index = 1; index < track.points.length; index += 1) {
    const start = track.points[index - 1];
    const end = track.points[index];
    const startAltitude = start.altitudeFt ?? end.altitudeFt ?? 0;
    const endAltitude = end.altitudeFt ?? start.altitudeFt ?? 0;
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [
          [start.longitude, start.latitude],
          [end.longitude, end.latitude],
        ],
      },
      properties: {
        index,
        altitudeFt: (startAltitude + endAltitude) / 2,
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

function endpointsToGeoJson(track: Track): GeoJSON.FeatureCollection {
  if (track.points.length === 0) return { type: 'FeatureCollection', features: [] };
  const first = track.points[0];
  const last = track.points[track.points.length - 1];
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [first.longitude, first.latitude] },
        properties: { role: 'start' },
      },
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [last.longitude, last.latitude] },
        properties: { role: 'end' },
      },
    ],
  };
}

export function addTrackLayer(map: MaplibreMap, track: Track): void {
  removeTrackLayer(map);

  const geojson = trackToGeoJson(track);
  map.addSource(SOURCE_ID, { type: 'geojson', data: geojson });

  // Each adjacent pair is a feature with its average altitude, allowing
  // MapLibre's data-driven expression to color the track along its route.
  const minAlt = track.stats.minAltFt;
  const maxAlt = Math.max(track.stats.maxAltFt, minAlt + 1);
  const midAlt = minAlt + (maxAlt - minAlt) / 2;
  map.addLayer({
    id: LINE_LAYER_ID,
    type: 'line',
    source: SOURCE_ID,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-width': 4,
      'line-color': [
        'interpolate', ['linear'], ['get', 'altitudeFt'],
        minAlt, '#22C55E',
        midAlt, '#F59E0B',
        maxAlt, '#EF4444',
      ],
    },
  });

  map.addSource(ENDPOINTS_SOURCE_ID, { type: 'geojson', data: endpointsToGeoJson(track) });
  map.addLayer({
    id: ENDPOINTS_LAYER_ID,
    type: 'circle',
    source: ENDPOINTS_SOURCE_ID,
    paint: {
      'circle-radius': 8,
      'circle-color': [
        'match', ['get', 'role'],
        'start', '#22C55E',
        'end',   '#EF4444',
        '#64748B',
      ],
      'circle-stroke-color': '#FFFFFF',
      'circle-stroke-width': 2,
    },
  });

  map.addSource(OWNSHIP_SOURCE_ID, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({
    id: OWNSHIP_LAYER_ID,
    type: 'circle',
    source: OWNSHIP_SOURCE_ID,
    paint: {
      'circle-radius': 10,
      'circle-color': '#3B82F6',
      'circle-stroke-color': '#FFFFFF',
      'circle-stroke-width': 2,
      'circle-opacity': 0.9,
    },
  });
}

export function removeTrackLayer(map: MaplibreMap): void {
  for (const id of [OWNSHIP_LAYER_ID, ENDPOINTS_LAYER_ID, LINE_LAYER_ID]) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  for (const id of [OWNSHIP_SOURCE_ID, ENDPOINTS_SOURCE_ID, SOURCE_ID]) {
    if (map.getSource(id)) map.removeSource(id);
  }
}

/** Position the ownship marker at a normalised fraction `t` (0..1) along the track. */
export function setOwnshipAtFraction(map: MaplibreMap, track: Track, t: number): void {
  if (track.points.length === 0) return;
  const clamped = Math.max(0, Math.min(1, t));
  const idx = Math.min(track.points.length - 1, Math.floor(clamped * track.points.length));
  const p = track.points[idx];
  const src = map.getSource(OWNSHIP_SOURCE_ID) as unknown as { setData: (d: GeoJSON.FeatureCollection) => void } | undefined;
  if (!src) return;
  src.setData({
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
      properties: { idx, altitudeFt: p.altitudeFt, groundSpeedKts: p.groundSpeedKts },
    }],
  });
}

/** Fit the camera to the track's bounding box. */
export function fitTrackOnMap(map: MaplibreMap, track: Track, padding = 60): void {
  if (track.points.length < 2) return;
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
  for (const p of track.points) {
    if (p.longitude < w) w = p.longitude;
    if (p.latitude  < s) s = p.latitude;
    if (p.longitude > e) e = p.longitude;
    if (p.latitude  > n) n = p.latitude;
  }
  map.fitBounds([[w, s], [e, n]], { padding });
}
